/**
 * Toasts and the confirm dialog.
 *
 * Both are app-wide singletons behind hooks, so a deeply nested row action can
 * say `toast.ok("Saved")` or `await confirm({...})` without threading callbacks
 * down through every page.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { Button } from "./primitives";
import { Modal } from "./overlays";

/* ---------------------------------------------------------------------- *
 * Toasts
 * ---------------------------------------------------------------------- */
type ToastKind = "info" | "ok" | "err";
interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
}

interface ToastApi {
  show: (text: string, kind?: ToastKind) => void;
  ok: (text: string) => void;
  err: (e: unknown) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <FeedbackProvider>");
  return ctx;
}

const ICONS: Record<ToastKind, "info" | "check" | "alert"> = {
  info: "info",
  ok: "check",
  err: "alert",
};

/* ---------------------------------------------------------------------- *
 * Confirm
 * ---------------------------------------------------------------------- */
interface ConfirmOptions {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}
type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <FeedbackProvider>");
  return ctx;
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const show = useCallback((text: string, kind: ToastKind = "info") => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, kind, text }]);
    // Errors linger — they carry something to read and act on.
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "err" ? 6500 : 3400);
  }, []);

  const toastApi = useMemo<ToastApi>(
    () => ({
      show,
      ok: (text) => show(text, "ok"),
      err: (e) => show(e instanceof Error ? e.message : String(e), "err"),
    }),
    [show],
  );

  const [pending, setPending] = useState<
    (ConfirmOptions & { resolve: (v: boolean) => void }) | null
  >(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      setPending((p) => {
        p?.resolve(value);
        return null;
      });
    },
    [],
  );

  return (
    <ToastCtx.Provider value={toastApi}>
      <ConfirmCtx.Provider value={confirm}>
        {children}

        {pending &&
          createPortal(
            <Modal
              title={pending.title}
              onClose={() => settle(false)}
              footer={
                <>
                  <Button variant="ghost" onClick={() => settle(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant={pending.danger ? "danger" : "primary"}
                    onClick={() => settle(true)}
                  >
                    {pending.confirmLabel ?? "Confirm"}
                  </Button>
                </>
              }
            >
              <div style={{ fontSize: "var(--t-md)", lineHeight: 1.6, color: "var(--text-2)" }}>
                {pending.body}
              </div>
            </Modal>,
            document.body,
          )}

        {createPortal(
          <div className="toasts">
            {toasts.map((t) => (
              <div key={t.id} className={`toast toast--${t.kind}`} role="status">
                <span className="toast__icon">
                  <Icon name={ICONS[t.kind]} size={16} />
                </span>
                <div>{t.text}</div>
              </div>
            ))}
          </div>,
          document.body,
        )}
      </ConfirmCtx.Provider>
    </ToastCtx.Provider>
  );
}
